import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

// Minimal inline-markdown renderer for hub-supplied copy (tool/prop
// descriptions), which the hub documents as inline markdown: **bold**,
// *italic*, `code`, [text](url). It builds React nodes rather than setting
// innerHTML, so it is XSS-safe by construction; anything it doesn't recognise
// is left as literal text. Not a block renderer — no lists, headings, or
// paragraphs (tool descriptions are single lines).

// Only linkable schemes; anything else renders as plain text (defends against
// javascript:/data: URLs slipping through as clickable links).
function safeHref(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === "http:" || u.protocol === "https:" || u.protocol === "mailto:" ? u.href : null;
  } catch {
    return null;
  }
}

type Rule = {
  re: RegExp;
  render: (m: RegExpMatchArray, key: string, inner: (s: string) => ReactNode[]) => ReactNode | null;
};

// Order matters: code first (its content is literal), then links, then bold
// before italic so `**` isn't consumed as two italics.
const RULES: Rule[] = [
  {
    re: /`([^`]+)`/,
    render: (m, key) => (
      <code key={key} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
        {m[1]}
      </code>
    ),
  },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, key, inner) => {
      const href = safeHref(m[2]);
      if (!href) return null; // fall back to literal text
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          {inner(m[1])}
        </a>
      );
    },
  },
  {
    // Bare URL autolink. Runs after the []() rule, so a markdown link (whose
    // match starts earlier, at the "[") is preferred. Trailing sentence
    // punctuation is left out of the link by requiring a non-punctuation last
    // char. safeHref still gates the scheme.
    re: /https?:\/\/[^\s]*[^\s.,;:!?)\]}]/,
    render: (m, key) => {
      const href = safeHref(m[0]);
      if (!href) return null;
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-foreground break-all"
        >
          {m[0]}
        </a>
      );
    },
  },
  {
    re: /\*\*([^*]+)\*\*/,
    render: (m, key, inner) => (
      <strong key={key} className="font-medium text-foreground">
        {inner(m[1])}
      </strong>
    ),
  },
  {
    // Asterisk-only: `_` is left alone so snake_case identifiers in a
    // description aren't mangled into emphasis (GitHub markdown does the same).
    re: /\*([^*]+)\*/,
    render: (m, key, inner) => <em key={key}>{inner(m[1])}</em>,
  },
];

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  // Earliest match across all rules; ties go to the earlier rule (code wins).
  let best: { index: number; rule: Rule; m: RegExpMatchArray } | null = null;
  for (const rule of RULES) {
    const m = rule.re.exec(text);
    if (m && m.index !== undefined && (best === null || m.index < best.index)) {
      best = { index: m.index, rule, m };
    }
  }
  if (!best) return text ? [text] : [];

  const { index, rule, m } = best;
  const key = `${keyPrefix}-${index}`;
  const node = rule.render(m, key, (s) => renderInline(s, `${key}i`));
  const before = text.slice(0, index);
  const after = text.slice(index + m[0].length);
  // A rule that declined (e.g. unsafe link) leaves the raw match as text.
  const middle: ReactNode[] = node === null ? [m[0]] : [node];
  return [...(before ? [before] : []), ...middle, ...renderInline(after, `${keyPrefix}n`)];
}

export function InlineMarkdown({ text, className }: { text: string; className?: string }) {
  return <span className={cn(className)}>{renderInline(text, "md")}</span>;
}
